// src/pages/SoftSkillsResults.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/SoftSkillsResults.css";
import 'bootstrap-icons/font/bootstrap-icons.css';
import logoMirai from "../assets/mirai_logo.png";
import Navbar from '../components/Navbar';
import { PLATFORM_PDF_FOOTER } from '../constants/platform';

// React-PDF
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFDownloadLink,
  Image,
} from "@react-pdf/renderer";

// === Estilos del PDF ===
const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 12, fontFamily: "Helvetica" },
  header: { textAlign: "center", marginBottom: 20 },
  title: { fontSize: 18, fontWeight: "bold", color: "#004d40", marginTop: 10 },
  logo: { width: 60, height: 60, margin: "0 auto" },
  studentInfo: { marginBottom: 20 },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginTop: 15,
    marginBottom: 8,
    color: "#333",
  },
  skillCard: {
    border: "1pt solid #ccc",
    borderRadius: 6,
    padding: 8,
    marginBottom: 10,
  },
  skillHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  skillName: { fontSize: 12, fontWeight: "bold" },
  level: { fontSize: 12, fontWeight: "bold" },
  recommendation: { fontSize: 11, marginTop: 4, textAlign: "justify" },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 40,
    right: 40,
    fontSize: 9,
    textAlign: "center",
    color: "#666",
  },
});

// === Documento PDF ===
const ResultsPDF = ({ user, results, skillAdvice }) => {
  const date = new Date().toLocaleDateString();
  const habilidades = results.results;

  // reutilizamos tu getLevelColor
  const getLevelColor = (level) => {
    switch (level) {
      case "Nivel muy bajo": return "#dc3545";
      case "Nivel bajo": return "#ff9800";
      case "Nivel medio": return "#ffc107";
      case "Nivel alto": return "#4caf50";
      case "Nivel muy alto": return "#28a745";
      default: return "#6c757d";
    }
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Marca de agua */}
        <Image
          src={logoMirai}
          style={{
            position: "absolute",
            top: "30%",
            left: "20%",
            width: 300,
            height: 300,
            opacity: 0.1,
          }}
        />

        {/* Header */}
        <View style={styles.header}>
          <Image src={logoMirai} style={styles.logo} />
          <Text style={styles.title}>Resultados de Habilidades Blandas</Text>
        </View>

        {/* Datos estudiante */}
        <View style={styles.studentInfo}>
          <Text>Nombre del estudiante: {user?.name || "N/D"}</Text>
          <Text>Fecha: {date}</Text>
          <Text>Total Score: {results.totalScore}</Text>
          <Text>Nivel Institucional: {results.institutionalLevel}</Text>
        </View>

        {/* Detalle de habilidades */}
        <Text style={styles.sectionTitle}>Detalle de Habilidades:</Text>
        {Object.entries(habilidades).map(([skill, data]) => {
          const advice = skillAdvice[skill]?.[data.level];
          return (
            <View key={skill} style={styles.skillCard} wrap={false}>
              <View style={styles.skillHeader}>
                <Text style={styles.skillName}>{skill}</Text>
                <Text style={{ color: getLevelColor(data.level), fontWeight: "bold" }}>
                  {data.level}
                </Text>
              </View>
              {advice && (
                <>
                  <Text style={styles.recommendation}>
                    <Text style={{ fontWeight: "bold" }}>Retroalimentación: </Text>
                    {advice.description}
                  </Text>
                  <Text style={styles.recommendation}>
                    <Text style={{ fontWeight: "bold" }}>Recomendación: </Text>
                    {advice.recommendation}
                  </Text>
                </>
              )}
            </View>
          );
        })}

        {/* Footer */}
        <Text style={styles.footer}>
          {PLATFORM_PDF_FOOTER}
        </Text>
      </Page>
    </Document>
  );
};

const SoftSkillsResults = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/me`, {
          headers: { Authorization: token },
        });
        console.log("Datos recibidos:", response.data.softSkillsResults);
        setResults(response.data.softSkillsResults || {});
        setUser(response.data);
      } catch (error) {
        console.error("Error al obtener resultados:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, []);

  const getLevelColor = (level) => {
    switch (level) {
      case 'Nivel muy bajo':
        return '#dc3545';
      case 'Nivel bajo':
        return '#ff9800';
      case 'Nivel medio':
        return '#ffc107';
      case 'Nivel alto':
        return '#4caf50';
      case 'Nivel muy alto':
        return '#28a745';
      default:
        return '#6c757d';
    }
  };

  const skillAdvice = {
    "Cognitiva": {
      "Nivel muy bajo": {
        description: "Habilidad que hay que adquirir, si no la adquieres puede ocasionarte dificultades tu vida personal, académica y laboral, es básica con ella puedes dar significado a la información, está relacionada con los procesos de aprendizaje, memoria, comunicación oral o escrita, el razonamiento, podrás tener un mejor desempeño en las áreas de matemáticas, física, económicas-administrativas, médico-biológicas, estadística, informática, en el área empresarial, de investigación, emprendimiento, entre otras.",
        recommendation: "Te recomendamos adquirirla mediante cursos y/o talleres relacionados con la resolución de problemas, de memorización, análisis de textos, armar rompecabezas, jugar ajedrez, realizar ejercicios de concentración, percepción, comprensión de lectura, ejercicios de razonamiento lógico matemático, entre otros."
      },
      "Nivel bajo": {
        description: "Habilidad que hay que adquirir, si no la adquieres puede ocasionarte dificultades tu vida personal, académica y laboral, es básica con ella puedes dar significado a la información, está relacionada con los procesos de aprendizaje, memoria, comunicación oral o escrita, el razonamiento, podrás tener un mejor desempeño en las áreas de matemáticas, física, económicas-administrativas, médico-biológicas, estadística, informática, en el área empresarial, de investigación, emprendimiento, entre otras.",
        recommendation: "Te recomendamos adquirirla mediante cursos y/o talleres relacionados con la resolución de problemas, de memorización, análisis de textos, armar rompecabezas, jugar ajedrez, realizar ejercicios de concentración, percepción, comprensión de lectura, ejercicios de razonamiento lógico matemático, entre otros."
      },
      "Nivel medio": {
        description: "Habilidad que necesitas fortalecer para un mayor éxito académico y profesional, es básica con ella puedes dar significado a la información, está relacionada con los procesos de aprendizaje, memoria, comunicación oral o escrita, el razonamiento, podrás tener un mejor desempeño en las áreas de matemáticas, física, económicas-administrativas, médico-biológicas, estadística, informática, en el área empresarial, de investigación, emprendimiento, entre otras.",
        recommendation: "Te recomendamos seguir desarrollándola mediante cursos y/o talleres relacionados con la resolución de problemas, de memorización, análisis de textos, armar rompecabezas, jugar ajedrez, realizar ejercicios de concentración, percepción, comprensión de lectura, ejercicios de razonamiento lógico matemático, entre otros."
      },
      "Nivel alto": {
        description: "Felicidades tienes desarrollada esta habilidad te sugerimos sigas fortaleciéndola. Con ella puedes llegar a ser historiador, (a), economista, matemático (a), físico (a), emprendedor (a), investigador (a), docente, entre otros.",
        recommendation: "Te recomendamos seguir fortaleciéndola mediante ejercicios de resolución de problemas, de memorización, análisis de textos, armar rompecabezas, jugar ajedrez, realizar ejercicios de concentración, percepción, comprensión de lectura, ejercicios de razonamiento lógico matemático, entre otros."
      },
      "Nivel muy alto": {
        description: "Felicidades tienes desarrollada esta habilidad te sugerimos sigas fortaleciéndola. Con ella puedes llegar a ser historiador, (a), economista, matemático (a), físico (a), emprendedor (a), investigador (a), docente, entre otros.",
        recommendation: "Te recomendamos seguir fortaleciéndola mediante ejercicios de resolución de problemas, de memorización, análisis de textos, armar rompecabezas, jugar ajedrez, realizar ejercicios de concentración, percepción, comprensión de lectura, ejercicios de razonamiento lógico matemático, entre otros."
      }
    },

    "Afectiva": {
      "Nivel muy bajo": {
        description: "Habilidad que hay que adquirir, si no la adquieres puede ocasionarte dificultades tu vida personal, académica y laboral, te ayudará a controlar tus emociones y sentimientos ante los problemas que se te presenten, podrás afrontar situaciones estresantes que requieran de autocontrol, podrás desarrollarte con más facilidad en las áreas médico-biológicas, sociales y administrativas, en actividades que requieran de liderazgo, vinculación y emprendimiento, entre otras. ",
        recommendation: "Te recomendamos adquirirla mediante cursos y/o talleres de auto reflexión, inteligencia emocional, pensamiento crítico, entre otros."
      },
      "Nivel bajo": {
        description: "Habilidad que hay que adquirir, si no la adquieres puede ocasionarte dificultades tu vida personal, académica y laboral, te ayudará a controlar tus emociones y sentimientos ante los problemas que se te presenten, podrás afrontar situaciones estresantes que requieran de autocontrol, podrás desarrollarte con más facilidad en las áreas médico-biológicas, sociales y administrativas, en actividades que requieran de liderazgo, vinculación y emprendimiento, entre otras. ",
        recommendation: "Te recomendamos adquirirla mediante cursos y/o talleres de auto reflexión, inteligencia emocional, pensamiento crítico, entre otros."
      },
      "Nivel medio": {
        description: "Habilidad que necesitas fortalecer para un mayor éxito académico y profesional, te ayudará a controlar tus emociones y sentimientos ante los problemas que se te presenten, podrás afrontar situaciones estresantes que requieran de autocontrol, podrás desarrollarte con más facilidad en las áreas médico-biológicas, sociales y administrativas, en actividades que requieran de liderazgo, vinculación y emprendimiento, entre otras.",
        recommendation: "Te recomendamos seguir desarrollándola mediante cursos y/o talleres de auto reflexión, inteligencia emocional, pensamiento crítico, entre otros."
      },
      "Nivel alto": {
        description: "Felicidades tienes desarrollada esta habilidad te sugerimos sigas fortaleciéndola. Con ella puedes llegar a ser psicólogo (a), educador (a), docente, abogado (a), antropólogo (a), empresario (a), médico (a), administrador (a), vendedor (a), emprendedor (a), filósofo (a), teólogo (a), consejero (a), terapeuta, entre otros.",
        recommendation: "Te recomendamos seguir fortaleciéndola, con ejercicios de auto reflexión, inteligencia emocional, pensamiento crítico entre otros."
      },
      "Nivel muy alto": {
        description: "Felicidades tienes desarrollada esta habilidad te sugerimos sigas fortaleciéndola. Con ella puedes llegar a ser psicólogo (a), educador (a), docente, abogado (a), antropólogo (a), empresario (a), médico (a), administrador (a), vendedor (a), emprendedor (a), filósofo (a), teólogo (a), consejero (a), terapeuta, entre otros.",
        recommendation: "Te recomendamos seguir fortaleciéndola, con ejercicios de auto reflexión, inteligencia emocional, pensamiento crítico entre otros."
      }
    },

    "Social": {
      "Nivel muy bajo": {
        description: "Habilidad que hay que adquirir, si no la adquieres puede ocasionarte dificultades tu vida personal, académica y laboral, es imprescindible para mantener relaciones sociales saludables durante nuestras etapas de desarrollo, en no adquirirla puede ocasionarte aislamiento, inseguridad, baja autoestima, depresión, no te permitirá desenvolverte adecuadamente cuando tengas que realizar trabajo colaborativo.",
        recommendation: "Te recomendamos adquirirla mediante cursos y/o talleres de actuación, oratoria, baile, hablar en público, lectura, de resolución de conflictos, practicar actividades deportivas y/o culturales en grupo, realizar trabajo colaborativo, asistir a reuniones, entre otros"
      },
      "Nivel bajo": {
        description: "Habilidad que hay que adquirir, si no la adquieres puede ocasionarte dificultades tu vida personal, académica y laboral, es imprescindible para mantener relaciones sociales saludables durante nuestras etapas de desarrollo, en no adquirirla puede ocasionarte aislamiento, inseguridad, baja autoestima, depresión, no te permitirá desenvolverte adecuadamente cuando tengas que realizar trabajo colaborativo.",
        recommendation: "Te recomendamos adquirirla mediante cursos y/o talleres de actuación, oratoria, baile, hablar en público, lectura, de resolución de conflictos, practicar actividades deportivas y/o culturales en grupo, realizar trabajo colaborativo, asistir a reuniones, entre otros"
      },
      "Nivel medio": {
        description: "Habilidad que necesitas fortalecer para un mayor éxito académico y profesional, te ayudará a establecer relaciones adecuadas con otras personas, fortalecerá tu desempeño cuando y el logro de tus objetivos en todos los ámbitos de tu vida. Algunas de las áreas donde puedes desempeñarte con éxito son: relaciones públicas, periodismo, publicidad, producción audiovisual, recursos humanos, administración de negocios, medicina, trabajo social, entre otros.",
        recommendation: "Te recomendamos seguir desarrollándola mediante cursos y/o talleres de actuación, oratoria, baile, hablar en público, lectura, de resolución de conflictos, practicar actividades deportivas y/o culturales en grupo, realizar trabajo colaborativo, asistir a reuniones, entre otros"
      },
      "Nivel alto": {
        description: "Felicidades tienes desarrollada esta habilidad te sugerimos sigas fortaleciéndola. Con ella puedes desarrollarte en las áreas de: relaciones públicas, periodismo, publicidad, producción audiovisual, recursos humanos, administración de negocios, medicina, trabajo social, entre otros.",
        recommendation: "Te recomendamos seguir fortaleciéndola, asistiendo a reuniones sociales, realizar trabajo colaborativo, hablar frente al público, tomar cursos de resolución de problemas, oratoria, lectura, practicar alguna actividad deportiva y/o cultural en grupo."
      },
      "Nivel muy alto": {
        description: "Felicidades tienes desarrollada esta habilidad te sugerimos sigas fortaleciéndola. Con ella puedes desarrollarte en las áreas de: relaciones públicas, periodismo, publicidad, producción audiovisual, recursos humanos, administración de negocios, medicina, trabajo social, entre otros.",
        recommendation: "Te recomendamos seguir fortaleciéndola, asistiendo a reuniones sociales, realizar trabajo colaborativo, hablar frente al público, tomar cursos de resolución de problemas, oratoria, lectura, practicar alguna actividad deportiva y/o cultural en grupo."
      }
    },

    "Moral": {
      "Nivel muy bajo": {
        description: "Habilidad que hay que adquirir, si no la adquieres puede ocasionarte dificultades tu vida personal, académica y laboral, es imprescindible para que las actividades que realizas durante tu formación y tu vida profesional ofrezcan las garantías necesarias que se requieren para el bien común y la dignidad profesional. La moral y la ética profesional están ligadas a todas las áreas del conocimiento, sin embargo, la profesiones donde sobresalen son: las ciencias biológicas, de la salud, abogacía, psicología, ética,  sociología, entre otras. ",
        recommendation: "Te recomendamos adquirirla mediante cursos y/o talleres sobre temas de formación cívica, de ética profesional, comprensión crítica y razonamiento moral, integridad y valores, conocer los lineamientos y reglamentos donde realizas actividades e identidad institucional, entre otros."
      },
      "Nivel bajo": {
        description: "Habilidad que hay que adquirir, si no la adquieres puede ocasionarte dificultades tu vida personal, académica y laboral, es imprescindible para que las actividades que realizas durante tu formación y tu vida profesional ofrezcan las garantías necesarias que se requieren para el bien común y la dignidad profesional. La moral y la ética profesional están ligadas a todas las áreas del conocimiento, sin embargo, la profesiones donde sobresalen son: las ciencias biológicas, de la salud, abogacía, psicología, ética,  sociología, entre otras. ",
        recommendation: "Te recomendamos adquirirla mediante cursos y/o talleres sobre temas de formación cívica, de ética profesional, comprensión crítica y razonamiento moral, integridad y valores, conocer los lineamientos y reglamentos donde realizas actividades e identidad institucional, entre otros."
      },
      "Nivel medio": {
        description: "Habilidad que necesitas fortalecer para un mayor éxito académico y profesional, te ayudará a sobresalir en las áreas de las ciencias biológicas y de la salud, abogacía, psicología, ética, sociología, entre otras.",
        recommendation: "Te recomendamos seguir desarrollándola mediante cursos y/o talleres sobre temas de formación cívica, de ética profesional, comprensión crítica y razonamiento moral, integridad y valores, conocer los lineamientos y reglamentos donde realizas actividades e identidad institucional, entre otros."
      },
      "Nivel alto": {
        description: "Felicidades tienes desarrollada esta habilidad te sugerimos sigas fortaleciéndola. Con ella puedes desarrollarte en las áreas de las ciencias biológicas y de la salud, abogacía, psicología, ética, sociología, entre otras.",
        recommendation: "Te recomendamos seguir fortaleciéndola, participando en temas de formación cívica, ética, valores y principios, conociendo la normatividad de tu institución y cumplirla."
      },
      "Nivel muy alto": {
        description: "Felicidades tienes desarrollada esta habilidad te sugerimos sigas fortaleciéndola. Con ella puedes desarrollarte en las áreas de las ciencias biológicas y de la salud, abogacía, psicología, ética, sociología, entre otras.",
        recommendation: "Te recomendamos seguir fortaleciéndola, participando en temas de formación cívica, ética, valores y principios, conociendo la normatividad de tu institución y cumplirla."
      }
    },

    "Acometimiento": {
      "Nivel muy bajo": {
        description: "Habilidad que necesitas adquirir para que tengas un mejor rendimiento académico y profesional, con esta habilidad puedes desarrollarte en las áreas de ingeniería industrial, ingeniería en sistemas y tecnología de la información, desarrollo de software, diseño gráfico, mercadotecnia, emprendimiento, investigación, entre otras.",
        recommendation: "Te recomendamos adquirirla mediante cursos y/o talleres sobre temas de innovación y creatividad, de marketing, elaboración de diagramas y gráficos, dibujar, pensamiento crítico, armando logos, maquetas, entre otros."
      },
      "Nivel bajo": {
        description: "Habilidad que necesitas adquirir para que tengas un mejor rendimiento académico y profesional, con esta habilidad puedes desarrollarte en las áreas de ingeniería industrial, ingeniería en sistemas y tecnología de la información, desarrollo de software, diseño gráfico, mercadotecnia, emprendimiento, investigación, entre otras.",
        recommendation: "Te recomendamos adquirirla mediante cursos y/o talleres sobre temas de innovación y creatividad, de marketing, elaboración de diagramas y gráficos, dibujar, pensamiento crítico, armando logos, maquetas, entre otros."
      },
      "Nivel medio": {
        description: "Habilidad que necesitas fortalecer para un mayor éxito académico y profesional, te ayudará a sobresalir en las áreas de ingeniería industrial, ingeniería en sistemas y tecnología de la información, desarrollo de software, diseño gráfico, mercadotecnia, emprendimiento, investigación, entre otras.",
        recommendation: "Te recomendamos seguir desarrollándola mediante cursos y/o talleres sobre temas de innovación y creatividad, de marketing, elaboración de diagramas y gráficos, dibujar, pensamiento crítico, armando logos, maquetas, entre otros."
      },
      "Nivel alto": {
        description: "Felicidades tienes desarrollada esta habilidad te sugerimos sigas fortaleciéndola. Con ella puedes desarrollarte en las áreas de: ingeniería industrial, ingeniería en sistemas y tecnología de la información, desarrollo de software, diseño gráfico, mercadotecnia, emprendimiento, investigación, entre otras.",
        recommendation: "Te recomendamos seguir fortaleciéndola mediante el desarrollo de actividades utilizando tu creatividad e innovación, elaborando diagramas y gráficos, dibujar, armar logos y maquetas, abordar temas relacionados con el pensamiento crítico, entre otros."
      },
      "Nivel muy alto": {
        description: "Felicidades tienes desarrollada esta habilidad te sugerimos sigas fortaleciéndola. Con ella puedes desarrollarte en las áreas de: ingeniería industrial, ingeniería en sistemas y tecnología de la información, desarrollo de software, diseño gráfico, mercadotecnia, emprendimiento, investigación, entre otras.",
        recommendation: "Te recomendamos seguir fortaleciéndola mediante el desarrollo de actividades utilizando tu creatividad e innovación, elaborando diagramas y gráficos, dibujar, armar logos y maquetas, abordar temas relacionados con el pensamiento crítico, entre otros."
      }
    },

    "Directriz": {
      "Nivel muy bajo": {
        description: "Habilidad que necesitas adquirir para que tengas un mejor rendimiento académico y profesional, con esta habilidad puedes desarrollarte en las áreas económicas-administrativas, ciencias sociales, sanidad y salud, tecnología, informática, telecomunicaciones, comunicación, publicidad, relaciones públicas, industria, mecánica, electricidad, electrónica, investigación, docencia, emprendimiento.",
        recommendation: "Te recomendamos adquirirla mediante cursos y/o talleres de resolución de problemas complejos, pensamiento crítico, creatividad, gestión de personas, relaciones personales, comunicación asertiva, inteligencia emocional, juicio y toma de decisiones, orientación al servicio, negociación, entre otros."
      },
      "Nivel bajo": {
        description: "Habilidad que necesitas adquirir para que tengas un mejor rendimiento académico y profesional, con esta habilidad puedes desarrollarte en las áreas económicas-administrativas, ciencias sociales, sanidad y salud, tecnología, informática, telecomunicaciones, comunicación, publicidad, relaciones públicas, industria, mecánica, electricidad, electrónica, investigación, docencia, emprendimiento.",
        recommendation: "Te recomendamos adquirirla mediante cursos y/o talleres de resolución de problemas complejos, pensamiento crítico, creatividad, gestión de personas, relaciones personales, comunicación asertiva, inteligencia emocional, juicio y toma de decisiones, orientación al servicio, negociación, entre otros."
      },
      "Nivel medio": {
        description: "Habilidad que necesitas fortalecer para un mayor éxito académico y profesional, te ayudará a sobresalir en las áreas económicas-administrativas, ciencias sociales, sanidad y salud, tecnología, informática, telecomunicaciones, comunicación, publicidad, relaciones públicas, industria, mecánica, electricidad, electrónica, investigación, docencia, emprendimiento.",
        recommendation: "Te recomendamos seguir desarrollándola mediante cursos y/o talleres de resolución de problemas complejos, pensamiento crítico, creatividad, gestión de personas, relaciones personales, comunicación asertiva, inteligencia emocional, juicio y toma de decisiones, orientación al servicio, negociación, entre otros."
      },
      "Nivel alto": {
        description: "Felicidades tienes desarrollada esta habilidad te sugerimos sigas fortaleciéndola. Con ella puedes desarrollarte en las áreas económicas-administrativas, ciencias sociales, sanidad y salud, tecnología, informática, telecomunicaciones, comunicación, publicidad, relaciones públicas, industria, mecánica, electricidad, electrónica, investigación, docencia, emprendimiento.",
        recommendation: "Te recomendamos seguir fortaleciéndola, participando en temas y actividades de resolución de problemas complejos, pensamiento crítico, creatividad, gestión de personas, relaciones personales, comunicación asertiva, inteligencia emocional, juicio y toma de decisiones, orientación al servicio, negociación, entre otros."
      },
      "Nivel muy alto": {
        description: "Felicidades tienes desarrollada esta habilidad te sugerimos sigas fortaleciéndola. Con ella puedes desarrollarte en las áreas económicas-administrativas, ciencias sociales, sanidad y salud, tecnología, informática, telecomunicaciones, comunicación, publicidad, relaciones públicas, industria, mecánica, electricidad, electrónica, investigación, docencia, emprendimiento.",
        recommendation: "Te recomendamos seguir fortaleciéndola, participando en temas y actividades de resolución de problemas complejos, pensamiento crítico, creatividad, gestión de personas, relaciones personales, comunicación asertiva, inteligencia emocional, juicio y toma de decisiones, orientación al servicio, negociación, entre otros."
      }
    },

    "Gestión": {
      "Nivel muy bajo": {
        description: "Habilidad que necesitas adquirir para que tengas un mejor rendimiento académico y profesional, con esta habilidad puedes desarrollarte en las áreas de recursos humanos, administración de empresas, finanzas, comercio exterior, negocios internacionales, marketing digital, entre otros.",
        recommendation: "Te recomendamos adquirirla mediante cursos y/o talleres de resolución de problemas complejos, administración del tiempo, liderazgo, administración de recursos humanos, relaciones personales, comunicación asertiva, inteligencia emocional, juicio y toma de decisiones, orientación al servicio, negociación, entre otros."
      },
      "Nivel bajo": {
        description: "Habilidad que necesitas adquirir para que tengas un mejor rendimiento académico y profesional, con esta habilidad puedes desarrollarte en las áreas de recursos humanos, administración de empresas, finanzas, comercio exterior, negocios internacionales, marketing digital, entre otros.",
        recommendation: "Te recomendamos adquirirla mediante cursos y/o talleres de resolución de problemas complejos, administración del tiempo, liderazgo, administración de recursos humanos, relaciones personales, comunicación asertiva, inteligencia emocional, juicio y toma de decisiones, orientación al servicio, negociación, entre otros."
      },
      "Nivel medio": {
        description: "Habilidad que necesitas fortalecer para un mayor éxito académico y profesional, te ayudará a sobresalir en las áreas de recursos humanos, administración de empresas, finanzas, comercio exterior, negocios internacionales, marketing digital, entre otros.",
        recommendation: "Te recomendamos seguir desarrollándola mediante cursos y/o talleres de resolución de problemas complejos, administración del tiempo, liderazgo, administración de recursos humanos, relaciones personales, comunicación asertiva, inteligencia emocional, juicio y toma de decisiones, orientación al servicio, negociación, entre otros."
      },
      "Nivel alto": {
        description: "Felicidades tienes desarrollada esta habilidad te sugerimos sigas fortaleciéndola. Con ella puedes desarrollarte en las áreas de recursos humanos, administración de empresas, finanzas, comercio exterior, negocios internacionales, marketing digital, entre otros.",
        recommendation: "Te recomendamos seguir fortaleciéndola mediante actividades de resolución de problemas complejos, administración del tiempo, liderazgo, administración de recursos humanos, relaciones personales, comunicación asertiva, inteligencia emocional, juicio y toma de decisiones, orientación al servicio, negociación, entre otros."
      },
      "Nivel muy alto": {
        description: "Felicidades tienes desarrollada esta habilidad te sugerimos sigas fortaleciéndola. Con ella puedes desarrollarte en las áreas de recursos humanos, administración de empresas, finanzas, comercio exterior, negocios internacionales, marketing digital, entre otros.",
        recommendation: "Te recomendamos seguir fortaleciéndola mediante actividades de resolución de problemas complejos, administración del tiempo, liderazgo, administración de recursos humanos, relaciones personales, comunicación asertiva, inteligencia emocional, juicio y toma de decisiones, orientación al servicio, negociación, entre otros."
      }
    },

    "Alto potencial": {
      "Nivel muy bajo": {
        description: "Habilidad que necesitas adquirir para que tengas un mejor rendimiento académico y profesional, con esta habilidad puedes desarrollarte en las áreas de ingeniería ambiental, desarrollo de software, ciberseguridad, ingeniería robótica, marketing digital, ingeniería genética, salud mental, recursos humanos, geriatría, arquitectura, ciencia de los datos, entre otras.",
        recommendation: "Te recomendamos adquirirla mediante cursos y/o talleres de liderazgo, comunicación, administración de recursos humanos, resolución de problemas, toma de decisiones, administración del tiempo, trabajo colaborativo, gestión de proyectos, entre otros."
      },
      "Nivel bajo": {
        description: "Habilidad que necesitas adquirir para que tengas un mejor rendimiento académico y profesional, con esta habilidad puedes desarrollarte en las áreas de ingeniería ambiental, desarrollo de software, ciberseguridad, ingeniería robótica, marketing digital, ingeniería genética, salud mental, recursos humanos, geriatría, arquitectura, ciencia de los datos, entre otras.",
        recommendation: "Te recomendamos adquirirla mediante cursos y/o talleres de liderazgo, comunicación, administración de recursos humanos, resolución de problemas, toma de decisiones, administración del tiempo, trabajo colaborativo, gestión de proyectos, entre otros."
      },
      "Nivel medio": {
        description: "Habilidad que necesitas fortalecer para un mayor éxito académico y profesional, te ayudará a sobresalir en las áreas de ingeniería ambiental, desarrollo de software, ciberseguridad, ingeniería robótica, marketing digital, ingeniería genética, salud mental, recursos humanos, geriatría, arquitectura, ciencia de los datos, entre otras.",
        recommendation: "Te recomendamos seguir desarrollándola mediante cursos y/o talleres de liderazgo, comunicación, administración de recursos humanos, resolución de problemas, toma de decisiones, administración del tiempo, trabajo colaborativo, gestión de proyectos, entre otros."
      },
      "Nivel alto": {
        description: "Felicidades tienes desarrollada esta habilidad te sugerimos sigas fortaleciéndola. Con ella puedes desarrollarte en las áreas de ingeniería ambiental, desarrollo de software, ciberseguridad, ingeniería robótica, marketing digital, ingeniería genética, salud mental, recursos humanos, geriatría, arquitectura, ciencia de los datos, entre otras.",
        recommendation: "Te recomendamos seguir fortaleciéndola, realizando actividades en los temas de liderazgo, comunicación, administración de recursos humanos, resolución de problemas, toma de decisiones, administración del tiempo, trabajo colaborativo, gestión de proyectos, entre otros."
      },
      "Nivel muy alto": {
        description: "Felicidades tienes desarrollada esta habilidad te sugerimos sigas fortaleciéndola. Con ella puedes desarrollarte en las áreas de ingeniería ambiental, desarrollo de software, ciberseguridad, ingeniería robótica, marketing digital, ingeniería genética, salud mental, recursos humanos, geriatría, arquitectura, ciencia de los datos, entre otras.",
        recommendation: "Te recomendamos seguir fortaleciéndola, realizando actividades en los temas de liderazgo, comunicación, administración de recursos humanos, resolución de problemas, toma de decisiones, administración del tiempo, trabajo colaborativo, gestión de proyectos, entre otros."
      }
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p>Cargando resultados...</p>
        </div>
      );
    }

    if (!results || !results.results) {
      return (
        <div className="error-container">
          <i className="bi bi-exclamation-circle"></i>
          <p>No se encontraron resultados.</p>
        </div>
      );
    }

    const habilidades = results.results;

    switch (activeSection) {
      case 'details':
        return (
          <div className="results-section">
            <h3>
              <i className="bi bi-list-check"></i>
              Habilidades Blandas
            </h3>
            <div className="level-legend">
              <div className="legend-item">
                <span className="color-box" style={{ backgroundColor: '#dc3545' }}></span>
                <span>Nivel muy bajo</span>
              </div>
              <div className="legend-item">
                <span className="color-box" style={{ backgroundColor: '#ff9800' }}></span>
                <span>Nivel bajo</span>
              </div>
              <div className="legend-item">
                <span className="color-box" style={{ backgroundColor: '#ffc107' }}></span>
                <span>Nivel medio</span>
              </div>
              <div className="legend-item">
                <span className="color-box" style={{ backgroundColor: '#4caf50' }}></span>
                <span>Nivel alto</span>
              </div>
              <div className="legend-item">
                <span className="color-box" style={{ backgroundColor: '#28a745' }}></span>
                <span>Nivel muy alto</span>
              </div>
            </div>
            <div className="skills-grid">
              {Object.entries(habilidades).map(([skill, data]) => {
                const advice = skillAdvice[skill]?.[data.level];

                return (
                  <div key={skill} className="skill-card">
                    <div className="skill-header">
                      <h4>{skill}</h4>
                      <span className="level-indicator">{data.level}</span>
                    </div>
                    <div className="level-bar">
                      <div
                        className="level-fill"
                        style={{
                          width: `${(data.level / 5) * 100}%`,
                          backgroundColor: getLevelColor(data.level)
                        }}
                      />
                    </div>
                    {advice && (
                      <div className="skill-description mt-3">
                        <p style={{ textAlign: "justify" }}>
                          <p><strong>Retroalimentación:</strong> {advice.description}</p>
                          <p><strong>Recomendación:</strong> {advice.recommendation}</p>
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Botón de regreso al dashboard */}
            <button
              className="menu-item return-dashboard"
              onClick={() => navigate('/dashboard')}
            >
              <i className="bi bi-arrow-left-circle "></i>
              <p>Volver al Dashboard</p>
            </button>
          </div>
        );

      case 'overview':
      default:
        return (
          <div className="results-content">
            <div className="skills-summary">
              <h3>
                <i className="bi bi-graph-up"></i>
                Resumen de Habilidades
              </h3>
              <div className="skills-chart">
                {Object.entries(habilidades).map(([skill, data]) => (
                  <div key={skill} className="chart-bar">
                    <div className="bar-label">
                      <span>{skill}</span>
                    </div>
                    <div className="bar-container">
                      <div
                        className="bar-fill"
                        style={{
                          width: `${(data.level / 5) * 100}%`,
                          backgroundColor: getLevelColor(data.level)
                        }}
                      >
                        {data.level}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ textAlign: "center" }}><strong>Haz clic en el siguiente botón de detalles para más información</strong></p>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '10vh' }}>
                <button
                  className="custom-btn "
                  onClick={() => setActiveSection('details')}
                >
                  <i className="bi bi-info-circle"></i> Detalles
                </button>
              </div>

            </div>

            <div className="results-actions text-center mt-4">
              {/* Botón para descargar PDF */}
              <div className="d-inline-block">
                <PDFDownloadLink
                  document={
                    <ResultsPDF
                      user={user}
                      results={results}
                      skillAdvice={skillAdvice}
                    />
                  }
                  fileName={`Resultados_SoftSkills_${new Date()
                    .toLocaleDateString()
                    .replaceAll("/", "_")}.pdf`}
                >
                  {({ loading }) =>
                    loading ? (
                      <button className="btn btn-secondary px-4 py-2">
                        Generando PDF...
                      </button>
                    ) : (
                      <button className="btn btn-primary px-4 py-2">
                        <i className="bi bi-download"></i> Descargar Resultados
                      </button>
                    )
                  }
                </PDFDownloadLink>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <Navbar />

      <div className="soft-skills-results-container">
        {/* Main Layout */}
        <div className="dashboard-layout">
          {/* Sidebar */}
          <div className="dashboard-sidebar">
            <div className="sidebar-header">
              <h3>Habilidades Blandas</h3>
            </div>

            <div className="sidebar-menu">
              <button
                className={`menu-item ${activeSection === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveSection('overview')}
              >
                <i className="bi bi-speedometer2"></i>
                Resumen
              </button>
              <button
                className={`menu-item ${activeSection === 'details' ? 'active' : ''}`}
                onClick={() => setActiveSection('details')}
              >
                <i className="bi bi-list-check"></i>
                Detalles
              </button>

              {/* Botón de regreso al dashboard */}
              <button
                className="menu-item return-dashboard"
                onClick={() => navigate('/dashboard')}
              >
                <i className="bi bi-arrow-left-circle"></i>
                Volver al Dashboard
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="dashboard-main">
            {renderContent()}
          </div>
        </div>
      </div>
    </>
  );
};

export default SoftSkillsResults;
